import React, { useEffect } from 'react'
import classnames from 'classnames'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import { qualificationMap, parseDateByPattern } from 'services/utils'
import { Amount } from 'components/Amount'
import { Button, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { withProfile, withBalance, withLocation } from 'redux/enhancers'
import menuIconsSprite from 'assets/img/menu-icons/sprite.svg'
import Diagnostics from './Diagnostics'
import style from './style.module.scss'

const ProfileSnippet = ({
  collapsed,
  user,
  intl: { formatMessage, locale },
  getProfileData,
  getAccountBalance,
  balanceAmount,
  balanceIncome,
  redirectTo,
}) => {
  useEffect(() => {
    getProfileData()
    getAccountBalance()
  }, [])

  const { avatarUrl, firstname, qualification, rating, activityEndOfTerm, certifiedDiagnostician } = user

  const intlQualification =
    qualification &&
    qualificationMap[qualification] &&
    formatMessage({ id: qualificationMap[qualification] })

  const activationIsExecuted = !!activityEndOfTerm
  const activationExpireDate = activationIsExecuted ? parseDateByPattern(activityEndOfTerm) : null
  const activationDaysLeft = activationIsExecuted
    ? activationExpireDate.diff(moment(), 'days')
    : null

  return (
    <div
      className={classnames(style.profileSnippet, {
        [style.collapsed]: collapsed,
      })}
    >
      <div className={style.main}>
        {avatarUrl ? (
          <div className={style.avatar} style={{ backgroundImage: `url(${avatarUrl})` }} />
        ) : (
          <svg className={style.avatar}>
            <use href={`${menuIconsSprite}#user-placeholder-small`} />
          </svg>
        )}
        <div className={style.info}>
          <div className={style.name}>{firstname}</div>
          <div className={style.qualification}>{intlQualification}</div>
        </div>
      </div>
      <div className={style.finance}>
        <div className={style.row}>
          <svg width={18} height={19} className={style.icon}>
            <use href={`${menuIconsSprite}#wallet`} />
          </svg>
          <div className={style.info}>
            <div className={style.caption}>
              {formatMessage({ id: 'menu.left.profile.available' })}
            </div>
            <div className={style.value}>
              <Amount value={balanceAmount} currencyCode="rur" />
            </div>
          </div>
        </div>
        <div className={style.row}>
          <svg width={15} height={21} className={classnames(style.icon, style.hold)}>
            <use href={`${menuIconsSprite}#hold`} />
          </svg>
          <div className={style.info}>
            <div className={style.caption}>{formatMessage({ id: 'menu.left.profile.income' })}</div>
            <div className={style.value}>
              <Amount value={balanceIncome} currencyCode="rur" />
            </div>
          </div>
        </div>

        <div className={classnames(style.row, style.activation)}>
          <svg
            width={20}
            height={21}
            className={classnames(style.icon, style.activationIcon, {
              [style.activationIsExecuted]: activationIsExecuted,
            })}
          >
            <use href={`${menuIconsSprite}#activation`} />
          </svg>
          <div className={style.info}>
            <div className={style.caption}>
              {formatMessage({ id: 'menu.left.profile.activation.title' })}
            </div>
            <div className={style.value}>
              {activationIsExecuted && (
                <Tooltip
                  title={
                    <div style={{ whiteSpace: 'pre' }}>
                      {formatMessage(
                        { id: 'menu.left.profile.activation.expiresAt' },
                        { date: activationExpireDate.format('DD MMM YYYY – hh:mm') },
                      )}
                    </div>
                  }
                >
                  <span
                    className={classnames(style.executed, {
                      [style.expiresSoon]: activationDaysLeft <= 7,
                    })}
                  >
                    {formatMessage(
                      { id: 'menu.left.profile.activation.daysLeft' },
                      { daysLeft: activationDaysLeft },
                    )}
                  </span>
                </Tooltip>
              )}
              {!activationIsExecuted && (
                <span className={style.notExecuted}>
                  {formatMessage({ id: 'menu.left.profile.activation.notExecuted' })}
                </span>
              )}
            </div>
          </div>
          <div className={style.action}>{/* TODO: next release - activation onboarding */}</div>
        </div>

        <div className={style.row}>
          <svg width={14} height={24} className={classnames(style.icon, style.rating)}>
            <use href={`${menuIconsSprite}#business-rating`} />
          </svg>
          <div className={style.info}>
            <div className={style.caption}>
              {formatMessage({ id: 'menu.left.profile.business-reputation' })}
            </div>
            <div className={style.value}>
              {rating}
              {/* <span className={style.delta}>+5.5</span> */}
            </div>
          </div>
          <div className={style.action}>
            <Tooltip
              title={formatMessage({ id: 'menu.left.profile.business-reputation-info-tooltip' })}
            >
              <Button
                onClick={() => redirectTo('business-reputation-intro')}
                icon={<InfoCircleOutlined />}
                size="small"
                shape="circle"
                type="link"
              />
            </Tooltip>
          </div>
        </div>

        {(certifiedDiagnostician === true) && (['ru', 'lv'].indexOf(locale) !== -1) && (
          <div className={style.row}>
            <Diagnostics collapsed={collapsed} />
          </div>
        )}
      </div>
    </div>
  )
}

export default withLocation(withProfile(withBalance(injectIntl(ProfileSnippet))))
